import React, { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import L from "leaflet";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Colored dot icons for employee pins
function makeIcon(color) {
  return L.divIcon({
    className: "lom-emp-pin",
    html: `<span style="display:inline-block;width:14px;height:14px;border-radius:9999px;background:${color};border:2px solid white;box-shadow:0 0 0 1px ${color};"></span>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

const statusColor = {
  on_time: "#10B981",
  present: "#10B981",
  late: "#F59E0B",
  overtime: "#002FA7",
  left_early: "#F97316",
  default: "#71717A",
};

/**
 * Read-only map: shows store circles + employee pins (today's clock-ins).
 * Used on Dashboard.
 */
export default function LiveMap({ stores = [], records = [], height = 380 }) {
  const center = useMemo(() => {
    // Center on first store, else fallback
    const s = stores[0];
    if (s && (s.latitude || s.longitude)) return [s.latitude, s.longitude];
    if (records.length && records[0].clock_in_lat) return [records[0].clock_in_lat, records[0].clock_in_lng];
    return [37.7749, -122.4194];
  }, [stores, records]);

  return (
    <div className="border border-zinc-200 rounded-sm overflow-hidden" data-testid="live-map">
      <MapContainer center={center} zoom={4} style={{ height, width: "100%" }} scrollWheelZoom={true}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {stores.map((s) =>
          s.latitude || s.longitude ? (
            <React.Fragment key={s.id}>
              <Marker position={[s.latitude, s.longitude]}>
                <Popup>
                  <div className="font-semibold">{s.name}</div>
                  <div className="text-xs text-zinc-500">{s.address}</div>
                </Popup>
              </Marker>
              <Circle
                center={[s.latitude, s.longitude]}
                radius={Math.min(s.radius_meters || 100, 50000)}
                pathOptions={{ color: "#09090b", fillColor: "#09090b", fillOpacity: 0.05, weight: 1 }}
              />
            </React.Fragment>
          ) : null
        )}
        {records.map((r) =>
          r.clock_in_lat ? (
            <Marker
              key={r.id}
              position={[r.clock_in_lat, r.clock_in_lng]}
              icon={makeIcon(statusColor[r.status] || statusColor.default)}
            >
              <Popup>
                <div className="font-semibold">{r.user_name}</div>
                <div className="text-xs text-zinc-500">{r.store_name}</div>
                <div className="text-xs mt-1">
                  Clocked in at {r.clock_in_at ? new Date(r.clock_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                </div>
                <div className="text-xs uppercase font-bold tracking-wider mt-1" style={{ color: statusColor[r.status] || statusColor.default }}>
                  {r.status}
                </div>
              </Popup>
            </Marker>
          ) : null
        )}
      </MapContainer>
    </div>
  );
}
